/** @jsxImportSource @emotion/react */
import { faCog, faFlashlight } from "@fortawesome/pro-regular-svg-icons";
import React, { useRef, useState } from "react";
import Webcam from "react-webcam";
import { Modal, ModalBody, ModalHeader } from "reactstrap";
import { BarcodeFilter } from "../../../actions/Tenants/config/constantsTyped";
import { toast } from "../../../utils/toast";
import { Button } from "../../Helper/Button";
import SingleSelect from "../SingleSelect";
import { useWebcam } from "./useWebcam";
import { useBarcodeScanner } from "./useBarcodeScanner";

export interface QRCodeScanProps {
  onScan: (decodedText: string) => void;
  barcode_filter?: BarcodeFilter | ((decodedText: string) => string);
  debugMode?: boolean;
}

export const QRCodeScan: React.FC<QRCodeScanProps> = ({
    onScan,
    barcode_filter,
    debugMode = false,
}) => {
    const webcamRef = useRef<Webcam>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const modalContainerRef = useRef<HTMLDivElement>(null);

    const [isLandscape, setIsLandscape] = useState(false);
    const [showAdvanced, setShowAdvanced] = useState(false);

    const { filter } = typeof barcode_filter === "object" ? barcode_filter : { filter: barcode_filter };

    const {
        devices,
        selectedDeviceId,
        videoConstraints,
        torchEnabled,
        toggleTorch,
        setCameraDevice,
        onUserMedia,
        visibleCameraOptions,
    } = useWebcam();

    useBarcodeScanner({
        webcamRef,
        canvasRef,
        onScan,
        filter,
        debugMode,
        setIsLandscape,
    });

    const onCameraError = (error) => {
        toast.error(
            `Failed to start camera: (${error.name}:${error.message}). Please stop any application that is using your camera.`,
            {
                toastId: "create-location",
                autoClose: 3500,
            }
        );
    };

    const scanBox = isLandscape
        ? { left: "20%", top: "30%", width: "60%", height: "40%" }
        : { left: "10%", top: "35%", width: "80%", height: "30%" };

    const overlayClipPath = isLandscape
        ? "polygon(0% 0%, 0% 100%, 20% 100%, 20% 30%, 80% 30%, 80% 70%, 20% 70%, 20% 100%, 100% 100%, 100% 0%)"
        : "polygon(0% 0%, 0% 100%, 10% 100%, 10% 35%, 90% 35%, 90% 65%, 10% 65%, 10% 100%, 100% 100%, 100% 0%)";

    return (
        <div className="w-100 position-relative" style={{ height: "400px" }}>
            <div className="relative w-100 h-100">
                <Webcam
                    ref={webcamRef}
                    screenshotFormat="image/png"
                    forceScreenshotSourceSize
                    videoConstraints={videoConstraints}
                    width="100%"
                    height="100%"
                    imageSmoothing={false}
                    onUserMedia={onUserMedia}
                    onUserMediaError={onCameraError}
                    screenshotQuality={1}
                />

                <div
                    className="position-absolute start-50 d-flex gap-2"
                    style={{ bottom: "10px", transform: "translateX(-50%)", zIndex: 1 }}
                >
                    {/* flash button */}
                    <Button color={torchEnabled ? "warning" : "secondary"} onClick={toggleTorch} icon={faFlashlight} aria-label="Flash" />

                    {/* advanced settings button */}
                    {devices.length > 1 && (
                        <div className="pb-3">
                            <Button
                                color="light"
                                icon={faCog}
                                onClick={() => setShowAdvanced((v) => !v)}
                            />
                        </div>
                    )}
                </div>


                <div
                    className="position-absolute top-0 left-0 w-100 h-100 pe-none"
                    style={{ zIndex: 0, backgroundColor: "rgba(0,0,0,0.3)", clipPath: overlayClipPath }}
                />

                <div
                    className="position-absolute z-1"
                    style={{ ...scanBox, border: "2px solid #00ff00" }}
                />

                <div ref={modalContainerRef} className="position-absolute bottom-0 start-0" />

                <Modal container={modalContainerRef} isOpen={showAdvanced} toggle={() => setShowAdvanced((v) => !v)} modalClassName="slide-up--mobile" backdropClassName="slide-up--mobile" centered>
                    <ModalHeader toggle={() => setShowAdvanced((v) => !v)}>
                        Advanced Settings
                    </ModalHeader>

                    <ModalBody>
                        <SingleSelect
                            label="Camera device:"
                            options={visibleCameraOptions}
                            post_value={visibleCameraOptions.find((o) => o.value === selectedDeviceId)?.value || undefined}
                            field="camera"
                            setValue={setCameraDevice}
                            isClearable={false}
                        />
                    </ModalBody>
                </Modal>
            </div>

            <canvas ref={canvasRef} className="border d-none mt-1" />
        </div>
    );
};

export default QRCodeScan;
